import { FC, useEffect } from 'react';
import './Slide.scss';
import ImageMap from '../ImageMap/ImageMap';
import Video from '../Video/Video';
import { InteractiveTourProps } from '../InteractiveTourProps';
import { useInteractiveTourContext } from '../InteractiveTourContext';

const Slide: FC<InteractiveTourProps> = ({ content }) => {
  const { currentSlide, setCurrentSlide, setShowIntro, hasIntro } =
    useInteractiveTourContext();

  const slide = content.slides.items.find(
    (item: any) => item.id === currentSlide,
  );
  const slideIndex = content.slides.items.findIndex(
    (item: any) => item.id === currentSlide,
  );
  const slideTotal = content.slides.items.length;
  const lastSlide = slideIndex + 1 === slideTotal;
  const progressValue = (100 * (slideIndex + 1)) / slideTotal;

  const handleMenuClick = () => {
    setShowIntro(true);
  };

  const handleForwardClick = () => {
    const nextSlide = content.slides.items[slideIndex + 1].id;
    setCurrentSlide(nextSlide);
  };

  const handlePreviousClick = () => {
    const previousSlide = content.slides.items[slideIndex - 1].id;
    setCurrentSlide(previousSlide);
  };

  const handleNextStepsClick = (newSlide: string) => {
    setCurrentSlide(newSlide);
  };

  const handleRestartClick = () => {
    if (hasIntro) {
      setShowIntro(true);
    } else {
      const nextSlide = content.slides.items[0].id;
      setCurrentSlide(nextSlide);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      document
        .getElementsByClassName('interactive-tour__slide-aside-title')[0]
        .setAttribute('tabindex', '0');
    }, 1000);
  }, [currentSlide]); // Ensure this effect runs when currentSlide changes

  return (
    <>
      <div className="bx--row">
        <div
          className={`interactive-tour__slide-aside-sidebar ${
            content?.horizontalLayout
              ? 'interactive-tour__slide-aside-horizontal interactive-tour__slide-aside-horizontal-content'
              : 'bx--col-lg-5 bx--col-xlg-4 bx--col-md-3 bx--col-sm-4'
          }`}
        >
          <div className="interactive-tour__slide-aside">
            <div className="interactive-tour__slide-aside-wrapper">
              <h4 className="interactive-tour__slide-aside-title">
                {slide.title}
              </h4>
              <progress
                value={progressValue}
                max="100"
                className="interactive-tour__slide-aside-progress"
              ></progress>
              <p
                dangerouslySetInnerHTML={{
                  __html: slide.description.props?.defaultMessage,
                }}
              ></p>
              {slide.nextSteps?.active ? (
                <div className="interactive-tour__slide-aside-nextsteps">
                  <p>{content.slides.nextStepsLabel}</p>
                  <a
                    href={
                      slide.nextSteps.isLink
                        ? slide.nextSteps.url.props?.defaultMessage
                        : '#nextStep'
                    }
                    target={slide.nextSteps.isLink ? '_blank' : '_self'}
                    onClick={(event) => {
                      event.preventDefault();

                      if (!slide.nextSteps.isLink) {
                        handleNextStepsClick(slide.nextSteps.location);
                      } else {
                        window.open(slide.nextSteps.url, '_blank');
                      }
                    }}
                  >
                    <strong>{slide.nextSteps.title}</strong>
                  </a>
                </div>
              ) : (
                ''
              )}
            </div>
            <div className="interactive-tour__slide-aside-button-container">
              <div>
                {hasIntro ? (
                  <button
                    className="interactive-tour__slide-aside-button-menu"
                    onClick={() => handleMenuClick()}
                  >
                    <svg
                      focusable="false"
                      preserveAspectRatio="xMidYMid meet"
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      aria-hidden="true"
                      className="bx--btn__icon"
                    >
                      <path d="M6.7 12.3L2.9 8.5 15 8.5 15 7.5 2.9 7.5 6.7 3.7 6 3 1 8 6 13z"></path>
                    </svg>
                    <span>{content.slides.menuLabel}</span>
                  </button>
                ) : (
                  ''
                )}
              </div>
              <div className="interactive-tour__slide-aside-button-menu-nav">
                <button
                  className="interactive-tour__slide-aside-button"
                  disabled={slideIndex == 0 ? true : false}
                  onClick={() => handlePreviousClick()}
                  aria-label={
                    content.slides.previousLabel.props?.defaultMessage
                  }
                >
                  <span className="interactive-tour__slide-aside-button-tooltip">
                    {content.slides.previousLabel}
                  </span>
                  <svg
                    focusable="false"
                    preserveAspectRatio="xMidYMid meet"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    aria-hidden="true"
                    width="32"
                    height="32"
                    viewBox="6 1 32 32"
                    role="img"
                    className="bx--btn__icon"
                  >
                    <path d="M10 16L20 6 21.4 7.4 12.8 16 21.4 24.6 20 26z"></path>
                  </svg>
                </button>

                {lastSlide ? (
                  <button
                    className="interactive-tour__slide-aside-button"
                    onClick={() => handleRestartClick()}
                    aria-label={
                      content.slides.restartLabel.props?.defaultMessage
                    }
                  >
                    <span className="interactive-tour__slide-aside-button-tooltip">
                      {content.slides.restartLabel}
                    </span>
                    <svg
                      focusable="false"
                      preserveAspectRatio="xMidYMid meet"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      fill="currentColor"
                      width="20"
                      height="20"
                      viewBox="0 0 32 32"
                      role="img"
                      className="bx--btn__icon"
                    >
                      <path d="M18,28A12,12,0,1,0,6,16v6.2L2.4,18.6,1,20l6,6,6-6-1.4-1.4L8,22.2V16H8A10,10,0,1,1,18,26Z"></path>
                    </svg>
                  </button>
                ) : (
                  <button
                    className="interactive-tour__slide-aside-button"
                    onClick={() => handleForwardClick()}
                    aria-label={
                      content.slides.forwardLabel.props?.defaultMessage
                    }
                  >
                    <span className="interactive-tour__slide-aside-button-tooltip">
                      {content.slides.forwardLabel}
                    </span>
                    <svg
                      focusable="false"
                      preserveAspectRatio="xMidYMid meet"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      aria-hidden="true"
                      width="32"
                      height="32"
                      viewBox="5 1 32 32"
                      role="img"
                      className="bx--btn__icon"
                    >
                      <path d="M22 16L12 26 10.6 24.6 19.2 16 10.6 7.4 12 6z"></path>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          className={`interactive-tour__slide-aside-horizontal interactive-tour__slide-aside-content ${
            content?.horizontalLayout
              ? 'interactive-tour__slide-aside-horizontal-image'
              : 'bx--col-lg-11 bx--col-xlg-12 bx--col-md-5 bx--col-sm-4 '
          }`}
        >
          {slide.asset.type === 'image' ? (
            <ImageMap content={slide.asset} />
          ) : (
            <Video content={slide.asset} />
          )}
        </div>
      </div>
    </>
  );
};

export default Slide;
